/**
 * Scan-module registry — the single source of truth for BeetleSense's
 * photo-scan modules (the "many doors, one app" funnel, PRD §7.5).
 *
 * Every surface that needs to know "what modules exist" — the unified scan
 * picker, the cross-sell strip, the public doors, and (next) the hero — reads
 * from this list instead of hardcoding its own copy. Adding a module is one
 * entry here, not a refactor across the UI.
 *
 * Design decisions (MODULE_INTEGRATION.md §9, ratified 2026-06-16):
 *   - v1 module set: beetle, fallenTree, wildlife, relascope.
 *   - carbon / EUDR stay destination pages, not registry modules.
 *   - Relascope is a separate app: `runnable: false` → it's surfaced as a
 *     handoff door, not run inside runMultiScan.
 *   - `kind` drives the honesty label: photo-ai = AI estimate,
 *     instrument = real optical measurement (Relascope).
 *
 * This file holds metadata only (no scan runners, no heavy imports) so it can
 * be imported by any component without pulling in the inference stack.
 */

import { Bug, TreePine, Binoculars, Ruler, Waves, type LucideIcon } from 'lucide-react'

/** All modules, including the not-yet-integrated Relascope and acoustic sensor. */
export type ModuleId = 'beetle' | 'fallenTree' | 'wildlife' | 'relascope' | 'acoustic'

/**
 * The subset that runs inside the unified photo scan (runMultiScan). Relascope
 * is excluded — it's an external optical instrument handed off, not analysed
 * from the uploaded photo.
 */
export type AnalysisType = 'beetle' | 'fallenTree' | 'wildlife'

/**
 * The deployment tier of a blade — how wide it reaches and where it lives.
 * See docs/ARCHITECTURE_SPIDERWEB.md.
 *   - 'module'   : in-app door only (e.g. /acoustic). Cheapest leg.
 *   - 'landing'  : own public address + SEO + market-specific copy, same
 *                  codebase (a standalone-feeling door at /d/:id, optionally
 *                  fronted by its own domain via a Vercel rewrite).
 *   - 'microapp' : genuinely separate app/repo (native, hardware, partner),
 *                  connected back by the SSO + parcel + return-URL handoff.
 */
export type ModuleTier = 'module' | 'landing' | 'microapp'

export interface ScanModuleDef {
  id: ModuleId
  /** Public, no-login landing for this job-to-be-done. */
  doorRoute: string
  /** Deep-link into the unified scan with this module pre-selected (runnable only). */
  scanRoute?: string
  labelEn: string
  labelSv: string
  /** One-line value prop for cross-sell cards. */
  subEn: string
  subSv: string
  icon: LucideIcon
  /**
   * 'photo-ai' = honest AI estimate from a photo upload.
   * 'instrument' = real optical or acoustic measurement (Relascope, sensor node).
   */
  kind: 'photo-ai' | 'instrument'
  /** True when the module runs inside runMultiScan; false = external handoff. */
  runnable: boolean
  /** How wide this blade reaches / where it lives (spider-web model). */
  tier: ModuleTier
  /**
   * Tier 'landing'/'microapp': the public domain this blade is reachable at,
   * e.g. 'barkborre.beetlesense.ai' or 'relascope.beetlesense.ai'. A Vercel domain
   * rewrite points it at this blade. Undefined = reachable only in-app.
   */
  publicDomain?: string
  /**
   * Tier 'microapp': true when the blade is an external app reached via the
   * shared SSO + parcel-context + return-URL handoff (writes back to
   * parcel_scans). Undefined/false for in-codebase blades.
   */
  ssoHandoff?: boolean
  order: number
}

export const SCAN_MODULES: ScanModuleDef[] = [
  {
    id: 'beetle',
    doorRoute: '/scan',
    scanRoute: '/scan/multi?for=beetle',
    labelEn: 'Bark beetle',
    labelSv: 'Granbarkborre',
    subEn: 'Spot spruce bark beetle attack early',
    subSv: 'Upptäck angrepp tidigt',
    icon: Bug,
    kind: 'photo-ai',
    runnable: true,
    tier: 'landing',
    publicDomain: 'beetle.beetlesense.ai',
    order: 1,
  },
  {
    id: 'fallenTree',
    doorRoute: '/damage',
    scanRoute: '/scan/multi?for=fallenTree',
    labelEn: 'Fallen trees',
    labelSv: 'Stormfällda träd',
    subEn: 'Count fallen trees from a photo',
    subSv: 'Räkna stormfällda träd',
    icon: TreePine,
    kind: 'photo-ai',
    runnable: true,
    tier: 'landing',
    publicDomain: 'storm.beetlesense.ai',
    order: 2,
  },
  {
    id: 'wildlife',
    doorRoute: '/wildlife',
    scanRoute: '/scan/multi?for=wildlife',
    labelEn: 'Wildlife',
    labelSv: 'Vilt',
    subEn: 'Count game and see browsing pressure',
    subSv: 'Räkna vilt och betestryck',
    icon: Binoculars,
    kind: 'photo-ai',
    runnable: true,
    tier: 'landing',
    publicDomain: 'wildlife.beetlesense.ai',
    order: 3,
  },
  {
    id: 'relascope',
    doorRoute: '/relascope',
    // No scanRoute: Relascope is a separate optical-count app, handed off with
    // shared auth + parcel context + return URL (integration slot reserved).
    labelEn: 'Relascope',
    labelSv: 'Relaskop',
    subEn: 'Measure basal area and stocking',
    subSv: 'Mät grundyta och täthet',
    icon: Ruler,
    kind: 'instrument',
    runnable: false,
    // The textbook Tier-3 leg: a separate native/optical app on its own repo,
    // reached at its own subdomain, connected back via the SSO handoff contract.
    tier: 'microapp',
    publicDomain: 'relascope.beetlesense.ai',
    ssoHandoff: true,
    order: 4,
  },
  {
    id: 'acoustic',
    doorRoute: '/acoustic',
    // Addressed at its own subdomain (birdsong = woodpecker acoustics). hostTool
    // maps this host → doorRoute so birdsong.beetlesense.ai opens the door page.
    publicDomain: 'birdsong.beetlesense.ai',
    // No scanRoute: acoustic is a deployed sensor network, not a photo upload.
    labelEn: 'Acoustic monitoring',
    labelSv: 'Akustisk övervakning',
    subEn: 'Woodpecker activity = early beetle proxy',
    subSv: 'Hackspettsaktivitet = tidig barkborresignal',
    icon: Waves,
    kind: 'instrument',
    runnable: false,
    // Graduated module → landing: it now has its own door subdomain
    // (birdsong.beetlesense.ai), the tier that carries a publicDomain.
    tier: 'landing',
    order: 5,
  },
]

/** Modules that run inside the unified photo scan, in display order. */
export const RUNNABLE_MODULES = SCAN_MODULES
  .filter((m) => m.runnable)
  .sort((a, b) => a.order - b.order)

/** Runnable analysis ids, in order — replaces the old hand-kept ALL_ANALYSES. */
export const ALL_ANALYSES: AnalysisType[] = RUNNABLE_MODULES.map((m) => m.id as AnalysisType)

export function getModule(id: ModuleId): ScanModuleDef | undefined {
  return SCAN_MODULES.find((m) => m.id === id)
}

/**
 * Blades that have their own public address (a standalone landing or an
 * external micro-app), keyed for the Vercel domain-rewrite layer and the
 * spider-web sitemap. Each of these is reachable by an independent audience.
 */
export const ADDRESSED_MODULES = SCAN_MODULES.filter((m) => !!m.publicDomain)

/** Look up a blade by the public domain a visitor arrived on. */
export function getModuleByDomain(domain: string): ScanModuleDef | undefined {
  const host = domain.toLowerCase().replace(/^www\./, '')
  return SCAN_MODULES.find((m) => m.publicDomain?.toLowerCase() === host)
}
